import { ActiveUser } from "@/type/core/auth/auth.types";
import {
  TransactionDetail,
  TransactionType,
} from "@/type/core/transaction/transaction.types";
import TransactionButtonsDashboardSection from "@/views/core/extranet/transactions/details/sections/TransactionButtonsDasbhoardSection";
import BasicButton from "@components/core/buttons/BasicButton";
import BasicProgress from "@components/core/progress/BasicProgress";
import LockIcon from "@static/core/icons/icon-lock.svg?react";
import StopIcon from "@static/core/icons/icon-stop.svg?react";
import UnlockIcon from "@static/core/icons/icon-unlock.svg?react";
import { TRANSACTION_STATUS_ENUM } from "@utils/Constant";
import DateFormat from "@utils/DateFormat";
import { setSignatoryColor } from "@utils/helpers/signatories-helper";
import { ColumnsType } from "antd/lib/table/interface";
import parse from "html-react-parser";
import i18n from "i18next";
import { HTMLAttributes } from "react";

export const columns = (
  manage: (
    transactionId?: string,
    type?: TransactionType,
    status?: string,
  ) => void,
  setIsLoading: (isLoading: boolean) => void,
  refetchTransactions: () => void,
  user?: ActiveUser,
): ColumnsType<TransactionDetail> => [
  {
    dataIndex: "confidential",
    className: "px-0",
    render: (confidential: boolean, record: TransactionDetail) => {
      return !confidential ? (
        <>
          <span data-test="table-item" />
        </>
      ) : record.authorized ? (
        <UnlockIcon className="table-unlock" />
      ) : (
        <LockIcon className="table-lock" />
      );
    },
  },
  {
    title: i18n.t("home.dashboard.table.reference"),
    dataIndex: "object",
    className: "table-object",
    sorter: (a, b) => a.object?.localeCompare(b.object ?? "") ?? 1,
    showSorterTooltip: false,
    onCell: () => {
      return {
        /* eslint-disable @typescript-eslint/naming-convention */
        "data-test": "heading",
        "data-alt": i18n.t("home.dashboard.table.reference"),
        /* eslint-enable @typescript-eslint/naming-convention */
      } as HTMLAttributes<HTMLTableCellElement>;
    },
    render: (text: string) => {
      return <span data-test="table-item">{text}</span>;
    },
  },
  {
    title: i18n.t("home.dashboard.table.progression"),
    dataIndex: "type",
    className: "table-progress",
    onCell: () => {
      return {
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.stopPropagation();
        },
      };
    },
    render: (type: string, record: TransactionDetail) => {
      const totalNumberSignatories = record.signatories
        ? record.signatories.length
        : 1;
      const signedNumberSignatories = record.signatories
        ? record.signatories.filter(
            (signatory) => signatory.status === "signed",
          ).length
        : 1;
      return (
        type === "signature" &&
        record.status !== "draft" &&
        record.status !== "prepared" &&
        (record.signatureRequirementMode === "SOME" ? (
          i18n.t("home.dashboard.table.combined")
        ) : (
          <BasicProgress
            percent={(signedNumberSignatories / totalNumberSignatories) * 100}
            label={`${signedNumberSignatories}/${totalNumberSignatories}`}
          />
        ))
      );
    },
  },
  {
    title: i18n.t("home.dashboard.table.alias"),
    dataIndex: "officeName",
    sorter: (a, b) => a.officeName?.localeCompare(b.officeName ?? "") ?? 1,
    showSorterTooltip: false,
  },
  {
    title: i18n.t("home.dashboard.table.status"),
    dataIndex: "status",
    sorter: (a, b) => a.status?.localeCompare(b.status ?? "") ?? 1,
    showSorterTooltip: false,
    render: (status: string, record: TransactionDetail) => {
      let statusLabel = "";
      for (const key in TRANSACTION_STATUS_ENUM) {
        const statusEnum = TRANSACTION_STATUS_ENUM[key];
        if (statusEnum[0] === status) {
          statusLabel = statusEnum[1];
        }
      }
      return record.classified
        ? parse(TRANSACTION_STATUS_ENUM.CLASSIFIED[1])
        : parse(statusLabel);
    },
  },
  {
    title: i18n.t("home.dashboard.table.modificationDate"),
    dataIndex: "lastModificationDate",
    sorter: (a, b) =>
      a.lastModificationDate?.localeCompare(b.lastModificationDate ?? "") ?? 1,
    showSorterTooltip: false,
    render: (lastModificationDate: string) => {
      return DateFormat.dashboardDate(new Date(lastModificationDate));
    },
  },
  {
    title: i18n.t("home.dashboard.table.sendBy"),
    dataIndex: "sendBy",
  },
  {
    title: i18n.t("home.dashboard.table.signatories"),
    dataIndex: "type",
    className: "table-signatories",
    render: (type: string, record: TransactionDetail) => {
      return (
        <ul>
          {type === "scellement" && (
            <li>{i18n.t("home.dashboard.table.scellement.label")}</li>
          )}
          {type === "signature" &&
            record.signatories &&
            record.signatories.map((signatory, index) => (
              <li key={index} className={"custom-control-label"}>
                {setSignatoryColor(signatory, record.status ?? "")}
              </li>
            ))}
        </ul>
      );
    },
  },
  {
    title: i18n.t("home.dashboard.table.expirationDate"),
    dataIndex: "expirationDate",
    sorter: (a, b) =>
      a.expirationDate?.localeCompare(b.expirationDate ?? "") ?? 1,
    showSorterTooltip: false,
    render: (expirationDate: string) => {
      return (
        expirationDate && DateFormat.dashboardDate(new Date(expirationDate))
      );
    },
  },
  {
    title: "",
    dataIndex: "isStoppedTransaction",
    className: "px-0 cursor-default",
    render: (isStoppedTransaction: boolean) => {
      return isStoppedTransaction ? <StopIcon className="table-stop" /> : <></>;
    },
  },
  {
    title: "",
    dataIndex: "invitationMode",
    onCell: () => {
      return {
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.stopPropagation();
        },
      };
    },
    render: (invitationMode: string, record: TransactionDetail) => {
      const showParallelTransactionButton =
        invitationMode === "parallel" && record.status === "inProgress";

      const office = user?.offices.find(
        (office) => office.id === record.officeId,
      );
      const nextSignatory =
        record.signatories &&
        record.signatories.find((s) => s.status !== "signed");
      const nextSimultaneousSignatories = record.signatories?.filter(
        (s) => s.status === "simultaneous",
      );

      const transactionButtonsDashboardSection = (
        <TransactionButtonsDashboardSection
          transaction={record}
          refetchTransactions={refetchTransactions}
          setIsLoading={setIsLoading}
          officeRights={office?.rights ?? []}
          userEmail={user?.email}
          userSerialNumber={user?.certificateInfos?.serialNumber}
          nextSignatory={nextSignatory}
        />
      );

      if (
        showParallelTransactionButton &&
        nextSimultaneousSignatories &&
        nextSimultaneousSignatories.length > 1
      ) {
        return (
          <div className="d-flex flex-column gap-4">
            {transactionButtonsDashboardSection}
            <BasicButton
              text={i18n.t("home.dashboard.table.manage")}
              className="w-100"
              onClick={() => manage(record.id, record.type, record.status)}
            />
          </div>
        );
      } else {
        return transactionButtonsDashboardSection;
      }
    },
  },
];
